/**
 * Tide Dashboard - Main Application
 * Orchestrates data fetching, UI updates, and initialization
 * Supports multiple regions and stations
 */

import { CONFIG } from './config.js';
import { initSentry } from './sentry.js';
import { logger } from './logger.js';

// Initialize Sentry early for error tracking
initSentry();
import {
    AppState,
    initializeState,
    initializeSpotState,
    setCurrentRegion,
    setCurrentArea,
    setCurrentSpot,
    attachStationContext,
    findLocationForStation,
    isSpotMode,
    getCurrentSpot,
    getCurrentRegionId,
    getCurrentAreaId,
    getCurrentSpotSlug
} from './state.js';
import { loadRegions, loadSpots, getSpotByStationId, getSpotBySlug } from './config-loader.js';
import { initRouter, getInitialState, resolveStationHierarchy, updateUrl, updateSpotUrl } from './router.js';
import { parseRouteParts, resolveSpotRoute } from './route-utils.js';
import { DEFAULTS } from '../shared/defaults.mjs';
import {
    initializeSelectors,
    refreshSelectors,
    updatePageTitleForSpot,
    updateLocationButtonText
} from './ui-selectors.js';
import { syncSignupFormLocation } from './notifications.js';
import { recordRecentSpot } from './recent-spots.js';
import { initTheme, toggleTheme } from './theme.js';
import { initMap, setRefreshFunction, updateMapForRegion } from './map.js';
import { updateTideChart } from './chart.js';
import {
    fetchConditions,
    fetchTideCurve
} from './api.js';
import {
    updateOceanStatus,
    updateSwellConditions,
    updateTideStatus,
    updateWaterLevel,
    updateNextTides,
    updateWeather,
    updateSunTimes,
    updateTideHeroSection,
    updateBeachWindow,
    updateTimestamp,
    updateFreshnessFromConditions
} from './ui.js';
import { calculateOceanRating } from './rating.js';
import { analytics, initScrollDepthTracking } from './analytics.js';
import { createFatalErrorDisplay } from './dom-utils.js';

// Lazy-loaded modules - loaded on demand to reduce initial bundle size
let shareModule = null;
let notificationsInitialized = false;

// Expose functions to window for onclick handlers
// Note: switchStation is exposed by map.js to avoid circular dependency
window.toggleTheme = toggleTheme;
window.switchRegion = switchRegion;
window.switchArea = switchArea;
window.retryBuoyData = retryBuoyData;
window.retryWeatherData = retryWeatherData;

// Sequence counter to handle race conditions when switching stations rapidly
// Each new refresh increments this, invalidating any in-flight requests
let refreshSequence = 0;

// AbortController to cancel in-flight requests when location changes
let currentAbortController = null;

/**
 * Refresh all data from APIs
 * Uses Supabase /api/get-conditions as primary data source (single call)
 * Supplements with /api/curve for tide chart (needs 6-minute resolution)
 */
async function refreshAllData() {
    // Increment sequence - this invalidates any in-flight requests
    const currentSequence = ++refreshSequence;

    // Cancel any in-flight requests from previous refresh
    if (currentAbortController) {
        currentAbortController.abort();
    }
    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    const spotMode = isSpotMode();
    const currentSpot = getCurrentSpot();
    const stationId = AppState.currentStationId;
    const station = AppState.currentStation;

    if (spotMode ? !currentSpot : !station) {
        logger.error(spotMode ? 'No spot selected' : 'No station selected');
        return;
    }

    // Tide curve is still keyed by NOAA station; spot config carries the ID
    const tideStationId = spotMode
        ? (currentSpot.noaaStationId || stationId)
        : stationId;

    document.querySelectorAll('.card').forEach(card => {
        card.classList.add('loading');
    });

    try {
        const fetchStartTime = performance.now();

        // Primary: Get all conditions from Supabase (single call).
        // Spot mode fetches by slug (/api/get-conditions?spot=...), legacy
        // mode by region/area/station. api.js normalizes both response shapes.
        // Supplementary: Get tide curve for chart (needs higher resolution than Supabase stores)
        const [conditionsResult, curveResult] = await Promise.all([
            fetchConditions(null, null, null, spotMode
                ? { signal, spotSlug: currentSpot.slug }
                : { signal }),
            fetchTideCurve(tideStationId, { signal }).then(r => {
                logger.log(`Tide curve loaded${r?.fromCache ? ' (cached)' : ''}`);
                return r;
            })
        ]);

        // Check if this refresh is still current before updating UI
        if (currentSequence !== refreshSequence) {
            logger.log('Stale refresh discarded (newer request in progress)');
            return;
        }

        logger.log(`Total data fetch: ${(performance.now() - fetchStartTime).toFixed(0)}ms`);

        const conditions = conditionsResult?.data;
        const curveData = curveResult?.data || curveResult;

        if (!conditions) {
            // api.js already reported the underlying failure at the right severity
            logger.warn('No conditions available after fetch; leaving current view in place');
            return;
        }

        // Spot config for spot-specific scoring (swell windows, ideal
        // conditions, skill level). Spot mode already holds the normalized
        // spot in state (loaded once at init — no per-refresh spots.json
        // fetch); legacy mode resolves station → spot from the cached list.
        let spotConfig = currentSpot;
        if (!spotMode) {
            spotConfig = await getSpotByStationId(stationId);
            if (spotConfig) {
                setCurrentSpot(spotConfig);
            }
        }

        // Calculate ocean rating from conditions data
        // (Supabase stores simplified rating, we compute full details client-side)
        // Pass spot config for spot-specific scoring (swell windows, ideal conditions, skill level)
        const oceanRating = calculateOceanRating(
            conditions.weather,
            conditions.predictions,
            conditions.buoy,
            conditions.waterLevel,
            spotConfig
        );
        updateOceanStatus(oceanRating);

        // Update prominent tide section in hero card
        updateTideHeroSection(conditions.waterLevel, conditions.predictions);
        updateBeachWindow(conditions.waterLevel, conditions.predictions);

        // Update swell conditions
        updateSwellConditions(conditions.buoy);

        // Update sun times
        updateSunTimes(conditions.sunTimes);

        // Update other displays
        updateWaterLevel(conditions.waterLevel);
        updateTideStatus(conditions.waterLevel, conditions.predictions);
        updateNextTides(conditions.predictions);
        updateTideChart(curveData, conditions.predictions);
        updateWeather(conditions.weather);
        updateTimestamp();

        // Update share content (button text, aria labels) now that data is loaded
        // Only call if share module is already loaded (lazy-loaded on first click)
        if (shareModule) {
            shareModule.updateShareContent();
        }

        // Update freshness indicators from Supabase updated_at
        updateFreshnessFromConditions(conditions.updatedAt);

    } catch (error) {
        // Silently ignore abort errors (expected when switching locations)
        if (error.name === 'AbortError') {
            logger.log('Request cancelled (location changed)');
            return;
        }
        // Only log error if this is still the current request
        if (currentSequence === refreshSequence) {
            logger.error('Error refreshing data:', error);
        }
    } finally {
        // Only remove loading state if this is the current request
        if (currentSequence === refreshSequence) {
            document.querySelectorAll('.card').forEach(card => {
                card.classList.remove('loading');
            });
        }
    }
}

/**
 * Switch to a different region
 * @param {string} regionId - Region ID to switch to
 */
async function switchRegion(regionId) {
    if (regionId === getCurrentRegionId()) return;

    const region = AppState.regions[regionId];
    if (!region) {
        logger.error(`Region ${regionId} not found`);
        return;
    }

    // Update state (will set default area and station)
    setCurrentRegion(region);

    // Update URL with new hierarchy
    updateUrl(regionId, getCurrentAreaId(), AppState.currentStationId);

    // Update UI
    refreshSelectors(regionId, getCurrentAreaId(), AppState.currentStationId);

    // Update map for new area (ensure map is loaded if user navigates before scrolling to it)
    await ensureMapInitialized();
    await updateMapForRegion(AppState.currentArea, AppState.currentStationId);

    // Refresh data for new region
    await refreshAllData();
}

/**
 * Switch to a different area within the current region
 * @param {string} areaId - Area ID to switch to
 */
async function switchArea(areaId) {
    if (areaId === getCurrentAreaId()) return;

    const area = AppState.currentRegion?.areas?.[areaId];
    if (!area) {
        logger.error(`Area ${areaId} not found in current region`);
        return;
    }

    // Update state (will set default station for area)
    setCurrentArea(area);

    // Update URL
    updateUrl(getCurrentRegionId(), areaId, AppState.currentStationId);

    // Update UI
    refreshSelectors(getCurrentRegionId(), areaId, AppState.currentStationId);

    // Update map for new area (ensure map is loaded if user navigates before scrolling to it)
    await ensureMapInitialized();
    await updateMapForRegion(area, AppState.currentStationId);

    // Refresh data for new area
    await refreshAllData();
}

// Canonical origin for SEO tags (always the production domain)
const SITE_ORIGIN = 'https://surf-status.com';

/**
 * Point the canonical <link> at the pretty URL for a spot
 * (og-meta edge function does the same server-side for crawlers)
 * @param {string|null} slug - Spot slug, or null for the site root
 */
function updateCanonicalLink(slug) {
    const link = document.querySelector('link[rel="canonical"]');
    if (link) {
        link.setAttribute('href', slug ? `${SITE_ORIGIN}/${slug}` : `${SITE_ORIGIN}/`);
    }
}

/**
 * Apply a spot to app state and UI chrome (no data refresh)
 * @param {Object} spot - Normalized spot object (from config-loader)
 * @param {Object} options
 * @param {boolean} options.updateHistory - Push/replace the /{slug} URL
 * @param {boolean} options.replace - Use replaceState instead of pushState
 */
async function applySpot(spot, { updateHistory = true, replace = false } = {}) {
    setCurrentSpot(spot);

    // Attach the legacy Region → Area → Station hierarchy for this spot's
    // station WITHOUT flipping the mode flag (setCurrentStation would force
    // useSpotMode = false and silently kill spot mode). Cross-region lookup:
    // the spot's station may live in a different region than the current one.
    if (spot.noaaStationId) {
        const location = findLocationForStation(spot.noaaStationId, AppState.regions);
        if (location) {
            attachStationContext(location.regionId, location.areaId, location.stationId);
        } else {
            // Station missing from regions.json (a handful of spots use
            // stations the legacy registry never had). Still record the ID so
            // tide-curve fetches and per-station cache keys stay correct —
            // otherwise two such spots would collide on a stale cache key.
            AppState.currentStationId = spot.noaaStationId;
            AppState.currentStation = null;
            logger.warn(`No station hierarchy found for ${spot.noaaStationId} (${spot.slug})`);
        }
    }

    // Canonical pretty URL + SEO canonical tag
    if (updateHistory) {
        updateSpotUrl(spot.slug, replace);
    }
    updateCanonicalLink(spot.slug);

    // Update UI chrome
    updateLocationButtonText();
    updatePageTitleForSpot(spot);

    // Record in the picker's Recents — applySpot is the single choke point
    // where a spot becomes current (user switches AND initial URL load)
    recordRecentSpot({ slug: spot.slug, name: spot.name, region: spot.region });
}

/**
 * Switch to a different spot
 * @param {string} spotSlug - Spot slug to switch to
 * @param {Object} options - { updateHistory, replace } (see applySpot)
 */
async function switchSpot(spotSlug, options = {}) {
    // Skip no-op switches (but not before the first spot is applied)
    if (spotSlug === getCurrentSpotSlug() && getCurrentSpot()) return;

    const spot = await getSpotBySlug(spotSlug);
    if (!spot) {
        logger.error(`Spot ${spotSlug} not found`);
        return;
    }

    // Update state, URL, canonical tag, and UI chrome
    await applySpot(spot, options);

    // Sync map to the spot's area (cross-region moves fly to the new area)
    await ensureMapInitialized();
    if (AppState.currentArea) {
        await updateMapForRegion(AppState.currentArea, AppState.currentStationId);
    }

    // Keep the signup form's spot selector in sync (only once it has
    // lazy-loaded; it reads the current spot when it initializes)
    if (notificationsInitialized) {
        syncSignupFormLocation();
    }

    // Refresh data for new spot
    await refreshAllData();
}

// Expose switchSpot to window for spot picker onclick handlers
window.switchSpot = switchSpot;

/**
 * Handle a route change from the router (browser back/forward, direct URLs)
 * @param {Object} route - { mode: 'spot'|'station'|'default', ... }
 */
async function handleRoute(route) {
    if (isSpotMode()) {
        // Spot mode: every route resolves to a spot
        if (route.mode === 'spot' && route.slug) {
            await switchSpot(route.slug, { updateHistory: false });
            return;
        }

        if (route.mode === 'station') {
            // Legacy history entry: map station → first spot for that station.
            // URL stays in its legacy form (content is correct; the next
            // user-driven switch writes the pretty form).
            const regionsData = await loadRegions();
            const { stationId } = resolveStationHierarchy(
                regionsData, route.region, route.area, route.station
            );
            const spot = stationId ? await getSpotByStationId(stationId) : null;
            await switchSpot(spot?.slug || DEFAULTS.spot, { updateHistory: false });
            return;
        }

        // Bare URL: default spot
        await switchSpot(DEFAULTS.spot, { updateHistory: false });
        return;
    }

    // Legacy station mode (spots data unavailable)
    await handleNavigation(route.region, route.area, route.station);
}

/**
 * Handle navigation (browser back/forward)
 * @param {string} regionId - Region ID from URL
 * @param {string} areaId - Area ID from URL
 * @param {string} stationId - Station ID from URL
 */
async function handleNavigation(regionId, areaId, stationId) {
    const targetRegionId = regionId || 'california';
    const region = AppState.regions[targetRegionId];

    if (!region) {
        logger.error(`Region ${targetRegionId} not found`);
        return;
    }

    // Determine target area
    const targetAreaId = areaId || region.defaultArea || Object.keys(region.areas || {})[0];
    const area = region.areas?.[targetAreaId];

    // Determine target station
    const targetStationId = stationId || area?.defaultStation;

    // Update state
    setCurrentRegion(region, targetAreaId, targetStationId);

    // Update UI
    refreshSelectors(targetRegionId, targetAreaId, targetStationId);

    // Update map for area (ensure map is loaded if user navigates before scrolling to it)
    await ensureMapInitialized();
    await updateMapForRegion(area, targetStationId);

    // Refresh data
    await refreshAllData();
}

/**
 * Retry fetching buoy data
 * Simplified: triggers full refresh from Supabase
 */
async function retryBuoyData() {
    const btn = document.querySelector('#swell-card .retry-btn');
    if (btn) btn.disabled = true;

    try {
        await refreshAllData();
        const errorEl = document.querySelector('#swell-card .error-state');
        if (errorEl) errorEl.remove();
    } catch (e) {
        logger.error('Retry failed:', e);
    }

    if (btn) btn.disabled = false;
}

/**
 * Retry fetching weather data
 * Simplified: triggers full refresh from Supabase
 */
async function retryWeatherData() {
    const btn = document.querySelector('#weather-card .retry-btn');
    if (btn) btn.disabled = true;

    try {
        await refreshAllData();
    } catch (e) {
        logger.error('Retry failed:', e);
    }

    if (btn) btn.disabled = false;
}

/**
 * Initialize chart visibility tracking
 * Fires view_tide_chart event when chart scrolls into view
 */
function initChartVisibilityTracking() {
    const chartEl = document.getElementById('tide-chart');
    if (!chartEl) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                analytics.viewTideChart();
                observer.disconnect(); // Fire once per page load
            }
        });
    }, { threshold: 0.5 });

    observer.observe(chartEl);
}

// Track if map has been initialized (for lazy loading)
let mapInitialized = false;
let mapObserver = null;

/**
 * Setup lazy loading for the map
 * Uses IntersectionObserver to only load Leaflet when map is near viewport
 */
function setupLazyMapLoading() {
    const mapEl = document.getElementById('map');
    if (!mapEl) return;

    mapObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !mapInitialized) {
                mapInitialized = true;
                initMap(AppState.currentArea);
                mapObserver.disconnect();
            }
        });
    }, { rootMargin: '200px' }); // Load slightly before visible

    mapObserver.observe(mapEl);
}

/**
 * Trigger map initialization immediately (when user switches location before map is visible)
 */
async function ensureMapInitialized() {
    if (mapInitialized) return;
    mapInitialized = true;
    if (mapObserver) {
        mapObserver.disconnect();
    }
    await initMap(AppState.currentArea);
}

/**
 * Setup lazy loading for share functionality
 * Loads share.js on first click of share button
 */
function setupLazyShareLoading() {
    const shareBtn = document.getElementById('share-btn');
    if (!shareBtn) return;

    // Prefetch on hover for faster load when clicked
    shareBtn.addEventListener('mouseenter', () => {
        if (!shareModule) {
            // Prefetch the module (browser will cache it)
            import('./share.js').catch(() => {});
        }
    }, { once: true });

    // Load and initialize on first click
    shareBtn.addEventListener('click', async (e) => {
        if (!shareModule) {
            e.preventDefault();
            shareModule = await import('./share.js');
            shareModule.initShare();
            // Trigger the click handler now that module is loaded
            shareModule.handleShareClick();
        }
    }, { once: true });
}

/**
 * Setup lazy loading for notifications signup form
 * Loads notifications.js when signup card approaches viewport
 */
function setupLazyNotificationsLoading() {
    const signupCard = document.getElementById('signup-card');
    if (!signupCard) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !notificationsInitialized) {
                notificationsInitialized = true;
                import('./notifications.js').then(mod => {
                    mod.initNotifications();
                });
                observer.disconnect();
            }
        });
    }, { rootMargin: '200px' }); // Load slightly before visible

    observer.observe(signupCard);
}

/**
 * Register service worker for PWA/offline support
 */
async function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
        try {
            // One-shot reload when a new worker takes control, so users pick
            // up a fresh deploy on their next visit instead of the one after.
            let reloaded = false;
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                if (reloaded || !navigator.serviceWorker.controller) return;
                reloaded = true;
                window.location.reload();
            });

            const registration = await navigator.serviceWorker.register('/sw.js', {
                updateViaCache: 'none'
            });
            logger.log('Service worker registered:', registration.scope);
        } catch (error) {
            logger.warn('Service worker registration failed:', error);
        }
    }
}

/**
 * Initialize spot-based navigation (primary mode)
 *
 * URL-resolution decision table (first match wins):
 *   /{slug}                        → that spot
 *   /?spot={slug}                  → that spot
 *   ?region/?area/?station params  → resolved station → first spot for it
 *   / (or anything unresolvable)   → DEFAULTS.spot
 * The URL is then normalized to /{slug} via replaceState.
 *
 * @param {Object} regionsData - regions.json data
 * @param {Object} spotsData - Normalized spots data (from loadSpots)
 */
async function initSpotMode(regionsData, spotsData) {
    const spots = spotsData.spots;
    const slugSet = new Set(spots.map(s => s.slug));

    // First spot per NOAA station (legacy station → spot mapping)
    const stationToSlug = new Map();
    for (const spot of spots) {
        if (spot.noaa_station_id && !stationToSlug.has(spot.noaa_station_id)) {
            stationToSlug.set(spot.noaa_station_id, spot.slug);
        }
    }

    // Parse URL and resolve legacy params to a station if present
    const parts = parseRouteParts(window.location);
    let resolvedStation = null;
    if (parts.region || parts.area || parts.station) {
        resolvedStation = resolveStationHierarchy(
            regionsData, parts.region, parts.area, parts.station
        ).stationId || null;
    }

    const route = resolveSpotRoute(parts, {
        hasSpot: (slug) => slugSet.has(slug),
        spotForStation: (id) => stationToSlug.get(id) || null,
        resolvedStation,
        defaultSpot: DEFAULTS.spot
    });
    logger.log(`Initial spot: ${route.slug} (via ${route.source})`);

    // Load the regions registry into state (legacy hierarchy is still needed
    // for maps/tide-curve context), then flip to spot mode
    initializeState(regionsData);
    initializeSpotState(spotsData, route.slug);

    // Apply the spot: state + hierarchy + canonical URL (replaceState so
    // legacy/?spot= URLs normalize to /{slug} without a history entry)
    const spot = await getSpotBySlug(getCurrentSpotSlug());
    if (!spot) {
        throw new Error(`Initial spot ${getCurrentSpotSlug()} not found in spots data`);
    }
    await applySpot(spot, { updateHistory: true, replace: true });
}

/**
 * Initialize legacy station-based navigation
 * Used only when spots data fails to load (full fallback)
 * @param {Object} regionsData - regions.json data
 */
function initLegacyMode(regionsData) {
    // Determine initial state from URL
    const { regionId, areaId, stationId } = getInitialState(regionsData);
    logger.log(`Initial location (legacy): ${regionId} / ${areaId} / ${stationId}`);

    // Initialize state
    initializeState(regionsData, regionId, areaId, stationId);

    // Update URL to canonical form (replaces current entry)
    updateUrl(getCurrentRegionId(), getCurrentAreaId(), AppState.currentStationId, true);
}

/**
 * Initialize the application
 */
async function init() {
    logger.log('Tide Dashboard initializing...');
    logger.log('Mode: Netlify Functions (/api/*)');

    // Register service worker for offline support
    registerServiceWorker();

    // Initialize theme first
    initTheme();

    try {
        // Load region + spot configuration in parallel (both cached fetches;
        // loadSpots is init/picker-only — never on the per-refresh hot path)
        const [regionsData, spotsData] = await Promise.all([
            loadRegions(),
            loadSpots()
        ]);
        logger.log(`Loaded ${Object.keys(regionsData.regions).length} regions`);

        if (spotsData?.spots?.length > 0) {
            // Primary: spot-based navigation with pretty URLs
            await initSpotMode(regionsData, spotsData);
        } else {
            // Fallback: full legacy station mode if spots data failed to load
            logger.warn('Spots data unavailable - using legacy station mode');
            initLegacyMode(regionsData);
        }

        // Initialize UI selectors
        initializeSelectors();

        // Set up map with refresh callback (lazy-loaded when in view)
        setRefreshFunction(refreshAllData);
        setupLazyMapLoading();

        // Set up URL router
        initRouter(handleRoute);

        // Setup lazy loading for below-fold features (reduces initial JS)
        setupLazyNotificationsLoading();
        setupLazyShareLoading();

        // Initialize analytics tracking (deferred to after critical path)
        if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
                initScrollDepthTracking();
                initChartVisibilityTracking();
            });
        } else {
            // Fallback for Safari
            setTimeout(() => {
                initScrollDepthTracking();
                initChartVisibilityTracking();
            }, 0);
        }

        // Initial data load
        await refreshAllData();

        // Set up auto-refresh
        setInterval(refreshAllData, CONFIG.refreshInterval);

        // Refresh on visibility change
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                refreshAllData();
            }
        });

        logger.log('Dashboard initialized successfully');
    } catch (error) {
        logger.error('Failed to initialize dashboard:', error);
        // Show error state to user using safe DOM methods
        document.body.innerHTML = '';
        document.body.appendChild(createFatalErrorDisplay(error));
    }
}

// Start the application
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
